import * as PIXI from "pixi.js";
import {AbstractGameState} from "./AbstractGameState";
import {main} from "../Layout/IntroScreenLayout";
import {Container} from "../Core/Container";
import {Sprite} from "../Core/Sprite";
import {Button} from "../GameObjects/Button";
import {Animatable, Ease} from "../Core/Animatable";
import {StateName} from "../Data/StateName";
import {updateStateAssets} from "../Data/Assets";
import {VoiceOverManager} from "../Managers/VoiceOverManager";
import {playVO, ReactionVO, voOptionsIdle} from "../Data/VoiceOver";
import {ButtonMute} from "../GameObjects/ButtonMute";
import {LunaCharacter} from "../GameObjects/LunaCharacter";
import {array, Callback} from "../Core/Reflection";
import {Keyboard} from "../GameObjects/Keyboard";
import {BaseButton} from "../Core/Button";
import {PackageContextMenu} from "../GameObjects/PackageContextMenu";
import {PackageModel} from "../Data/ItemModel";
import {Analytics} from "../Managers/Analytics";
import {Environment} from "../Data/Environment";

export class IntroScreen extends AbstractGameState {

    public readonly main = this.addChild(new Container());
    public readonly background = this.main.addChild(new Sprite());
    public readonly earthContainer = this.main.addChild(new Sprite());
    public readonly earth = array(() => this.earthContainer.addChild(new Sprite()));
    public readonly boxes = this.main.addChild(new Sprite());
    public readonly characters = this.main.addChild(new Sprite());
    public readonly luna = this.main.addChild(new LunaCharacter());
    public readonly buttonNext = this.main.addChild(new Button());
    public readonly buttonMute = this.addChild(new ButtonMute());

    private _earthIndex = 0;
    private _debugPackage?: PackageModel;
    private _idleInterval?: number;

    init(pSize: PIXI.Point, pData?: any) {
        super.init(pSize, pData);

        main.call(this);
        this.main.scale.set(0.5);

        this.luna.scale.set(1.25);
        this.earth.forEach((it, i) => it.alpha = i === 0 ? 1 : 0);

        this.buttonNext.visible = false;
        this.buttonNext.onClick.add(this.onClickButtonNext, this);

        const items = [this.luna, this.boxes, this.characters];
        items.forEach(it => it.visible = false);

        Keyboard.add(
            this.buttonNext,
            this.buttonMute
        );

        const visited = this.gameManager.visited;
        if (visited) {
            playVO("LetsGoLuna_012", () => {
                this.animateEarth();
                playVO([0.5, "LetsGoLuna_001", 0.5, ...ReactionVO(), "LetsGoLuna_002"], () => this.startIdleInterval());
            });
            this.showItems([...items, this.buttonNext]);
            Keyboard.focus(this.buttonNext);
        } else {
            playVO("LetsGoLuna_012", () => {
                this.showItems(items, () => {
                    this.animateEarth();
                    playVO(["LetsGoLuna_001", 0.5, ...ReactionVO(), "LetsGoLuna_002"], () => {
                        this.buttonNext.visible = true;
                        Keyboard.focus(this.buttonNext);
                        this.showItems([this.buttonNext]);
                        this.startIdleInterval();
                    });
                });
            });
        }

        Analytics.event("tutorial_begin");
    }

    destroy(pOptions?: Parameters<typeof PIXI.Container.prototype.destroy>[0]) {
        window.clearInterval(this._idleInterval);
        super.destroy(pOptions);
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        if (this.buttonMute) {
            this.buttonMute.setPosition(this._size);
        }
    }

    private onClickButtonNext(b: BaseButton, e?: PIXI.InteractionEvent) {
        this.gameManager.visited = true;
        if (this.debugChoosePackage(e, () => this.onClickButtonNext(b))) {
            return;
        }
        this.buttonNext.enabled = false;
        Keyboard.clear();
        VoiceOverManager.instance.stopVO();
        this.hideItems([this.buttonNext, this.characters, this.boxes, this.luna], () => {
            if (this._debugPackage) {
                updateStateAssets(this._debugPackage.stateName, this._debugPackage.itemData);
                this.gotoState(this._debugPackage.stateName, this._debugPackage.itemData);
            } else {
                updateStateAssets(StateName.Home);
                this.gotoState(StateName.Home);
            }
        });
    }

    private hideItems(items: (PIXI.Container & typeof Animatable)[], cb?: Callback) {
        let d = 0;
        items.forEach(it => {
            const y = it.y;
            it.moveYTo(this._size.x + it.height * 0.5, 0.2, Ease.sineIn, {
                delay: d += 0.1,
                onComplete: () => {
                    it.y = y;
                    it.visible = false;
                    if (cb && it === items[items.length - 1]) cb();
                }
            });
        });
    }

    private showItems(items: (PIXI.Container & typeof Animatable)[], cb?: Callback) {
        let d = 0;
        items.forEach(it => {
            it.visible = true;
            it.moveYFrom(this._size.x + it.height * 0.5, 0.4, Ease.backOut, {
                delay: d += 0.2,
                onComplete: () => {
                    if (cb && it === items[items.length - 1]) cb();
                }
            });
        });
    }

    private animateEarth() {
        let nextIndex = this._earthIndex + 1;
        if (nextIndex >= this.earth.length) {
            nextIndex = 0;
        }
        const prev = this.earth[this._earthIndex];
        const next = this.earth[nextIndex];
        const delay = 1.2;
        prev.alphaTo(0, 0.4, {delay: delay});
        next.alphaTo(1, 0.4, {
            delay: delay,
            onComplete: () => {
                this._earthIndex = nextIndex;
                this.animateEarth();
            }
        });
    }

    private debugChoosePackage(e?: PIXI.InteractionEvent, cb?: Callback) {
        if (e && e.data.originalEvent.which === 3 && !1) {
            new PackageContextMenu(e, model => {
                this._debugPackage = model;
                if (cb) cb();
            });
            return true;
        }
        return false;
    }

    private startIdleInterval() {
        let index = 0;
        window.clearInterval(this._idleInterval);
        this._idleInterval = window.setInterval(() => {
            playVO(index % 2 === 0 ? "LetsGoLuna_018" : [...ReactionVO(), "LetsGoLuna_002"], voOptionsIdle);
            index++;
        }, 20_000);
    }
}